import {
  selectJBPSettingsActualsSync,
  selectJBPSettingsCurrentFYear,
  useAvailableFYears,
} from 'legacy/lut/features/partner360/jbpSettings'
import { history } from 'legacy/lut/helpers/route'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { getUrlWithParams } from 'core-components/helpers/url'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import {
  formatCockpitActualsDate,
  getCockpitFiscalYearLabel,
  getCockpitFYearBounds,
  getCockpitPeriodLabel,
} from '../../../lib'
import {
  selectCockpitMode,
  selectEffectiveCockpitFYear,
  setCockpitFYear,
  setCockpitMetric,
  setCockpitPeriods,
} from '../../../model'
import { useEffectiveActualsSync } from '../../../model/hooks'
import {
  COCKPIT_METRIC_ORDER,
  selectAvailableCockpitMetrics,
  selectCockpitMetric,
  selectCockpitPeriods,
} from '../../../model/selectors'
import { CockpitPeriodKey } from '../../../model/types'
import type { ICockpitScopeChipOption } from '../../components/cockpitScopeChip'

import { useCockpitCurrency } from './useCockpitCurrency'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

/** `fullYear` and `ytd` are each exclusive with everything else; quarters combine freely. */
const EXCLUSIVE_KEYS: CockpitPeriodKey[] = ['fullYear', 'ytd']
const QUARTER_KEYS: CockpitPeriodKey[] = ['q1', 'q2', 'q3', 'q4']

/** Every label the period chip can rest on that is longest of its kind, so the chip is drawn at
 *  the width of the longest and keeps it whatever is picked. */
const PERIOD_SIZE_LABELS = ([['fullYear'], ['ytd'], QUARTER_KEYS] as CockpitPeriodKey[][]).map(
  getCockpitPeriodLabel
)

/** The page's scope — year, period, metric, currency — with the options each one offers and the
 *  handlers that change it. Shared by every control that draws the scope, so they all follow the
 *  same rules: quarters combine, Full Year and YTD stand alone, YTD exists only for the year whose
 *  actuals are being synced. */
export const useCockpitScope = () => {
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const currentFYear = useSelector(selectJBPSettingsCurrentFYear)
  const availableFYears = useAvailableFYears()
  const cockpitMode = useSelector(selectCockpitMode)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const actualsSync = useSelector(selectJBPSettingsActualsSync)
  const { isCurrentFYear } = useEffectiveActualsSync()
  const selectedMetric = useSelector(selectCockpitMetric)
  const availableMetrics = useSelector(selectAvailableCockpitMetrics)
  const { currency, availableCurrencies, isCurrencyDisabled, selectCurrency } = useCockpitCurrency()

  const { minFYear, maxFYear } = useMemo(
    () => getCockpitFYearBounds(currentFYear, availableFYears),
    [currentFYear, availableFYears]
  )

  const yearOptions = useMemo<ICockpitScopeChipOption[]>(() => {
    const years: ICockpitScopeChipOption[] = []

    for (let year = maxFYear; year >= minFYear; year -= 1) {
      years.push({ key: String(year), label: getCockpitFiscalYearLabel(year) })
    }

    return years
  }, [maxFYear, minFYear])

  const selectYear = useCallback(
    (nextFYear: number) => {
      dispatch(setCockpitFYear(nextFYear))

      if (cockpitMode === 'partner') {
        history.push(getUrlWithParams({ fyear: nextFYear }))
      }
    },
    [cockpitMode, dispatch]
  )

  useEffect(() => {
    if (!isCurrentFYear && selectedPeriods.includes('ytd')) {
      dispatch(setCockpitPeriods(['fullYear']))
    }
  }, [dispatch, isCurrentFYear, selectedPeriods])

  const periodOptions = useMemo<ICockpitScopeChipOption[]>(() => {
    const options: ICockpitScopeChipOption[] = [
      { key: 'fullYear', label: <Translation text={KEYS.period.fullYear} span /> },
      ...QUARTER_KEYS.map((key) => ({ key, label: key.toUpperCase() })),
    ]

    if (isCurrentFYear) {
      options.push({
        key: 'ytd',
        label: (
          <Translation
            text={KEYS.period.ytd}
            replaceParams={[
              { key: '{date}', value: formatCockpitActualsDate(actualsSync.fDateActuals) },
            ]}
            span
          />
        ),
      })
    }

    return options
  }, [actualsSync.fDateActuals, isCurrentFYear])

  const selectPeriod = useCallback(
    (key: string) => {
      const periodKey = key as CockpitPeriodKey

      if (EXCLUSIVE_KEYS.includes(periodKey)) {
        dispatch(setCockpitPeriods([periodKey]))
        return
      }

      const quarters = selectedPeriods.filter((item) => !EXCLUSIVE_KEYS.includes(item))
      const nextQuarters = quarters.includes(periodKey)
        ? quarters.filter((item) => item !== periodKey)
        : [...quarters, periodKey]

      dispatch(setCockpitPeriods(nextQuarters.length > 0 ? nextQuarters : ['fullYear']))
    },
    [dispatch, selectedPeriods]
  )

  const selectMetric = useCallback(
    (key: string) => dispatch(setCockpitMetric(key as ActualsValueTypes)),
    [dispatch]
  )

  const resolvedMetric = selectedMetric

  return {
    fYear,
    fYearLabel: getCockpitFiscalYearLabel(fYear),
    isLatestFYear: fYear === currentFYear,
    latestFYear: currentFYear,
    latestFYearLabel: getCockpitFiscalYearLabel(currentFYear),
    yearOptions,
    selectYear,
    selectedPeriods,
    periodLabel: getCockpitPeriodLabel(selectedPeriods),
    periodSizeLabels: PERIOD_SIZE_LABELS,
    periodOptions,
    selectPeriod,
    metric: resolvedMetric,
    metricOptions: availableMetrics.map((metric) => ({ key: metric, label: metric })),
    /* Every metric, not only this offering's: switching offering tabs does not resize the chip. */
    metricSizeLabels: COCKPIT_METRIC_ORDER,
    selectMetric,
    currency,
    isCurrencyDisabled,
    currencyOptions: availableCurrencies.map((currency) => ({ key: currency, label: currency })),
    currencySizeLabels: availableCurrencies,
    selectCurrency,
  }
}
